import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { PageContainer } from "@/components/shared/page-container";
import { Button } from "@/components/ui/button";

// FSD imports
import {
  MOCK_CURRENT_USER,
  MOCK_OFFICE,
  MOCK_GROUPS,
  MOCK_STUDENTS_BY_GROUP,
  MOCK_PREVIOUS_GAME_PLAYERS,
  MOCK_EXISTING_GAMES,
} from "@/entities/competition";
import {
  calculateRecommendedLeague,
  type PendingSelection,
} from "@/entities/competition";
import {
  useGameState,
  useGameActions,
  useGameSetup,
  useLateJoin,
} from "@/features/competition";
import { CreateGameDialog, ResultsTable } from "@/widgets/competition";
import {
  GameHeader,
  ValidationWarnings,
  DebugInfo,
  ReadyStateView,
  InProgressStateView,
} from "@/views/competition-demo";

function CompetitionDemoPage() {
  // Saving/Starting state
  const [isSaving, setIsSaving] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  // Pending selections for player table
  const [pendingSelections, setPendingSelections] = useState<
    PendingSelection[]
  >([]);

  // Game state hook
  const gameState = useGameState();

  // Game setup hook
  const gameSetup = useGameSetup({
    existingGames: MOCK_EXISTING_GAMES,
    previousGamePlayersData: MOCK_PREVIOUS_GAME_PLAYERS,
    setLocalPlayers: gameState.setLocalPlayers,
    setLocalLeagues: gameState.setLocalLeagues,
    setLocalPromotionCount: gameState.setLocalPromotionCount,
    localPlayers: gameState.localPlayers,
    isDirty: gameState.isDirty,
  });

  // Game actions hook
  const gameActions = useGameActions({
    currentUser: MOCK_CURRENT_USER,
    localLeagues: gameState.localLeagues,
    setLocalLeagues: gameState.setLocalLeagues,
    localPlayers: gameState.localPlayers,
    setLocalPlayers: gameState.setLocalPlayers,
    savedLeagues: gameState.savedLeagues,
    setSavedLeagues: gameState.setSavedLeagues,
    savedPlayers: gameState.savedPlayers,
    setSavedPlayers: gameState.setSavedPlayers,
    localPromotionCount: gameState.localPromotionCount,
    setLocalPromotionCount: gameState.setLocalPromotionCount,
    savedPromotionCount: gameState.savedPromotionCount,
    setSavedPromotionCount: gameState.setSavedPromotionCount,
    setPreviousGameDate: gameState.setPreviousGameDate,
    setConfirmedPreviousGameDate: gameState.setConfirmedPreviousGameDate,
    savedPreviousGameDate: gameState.savedPreviousGameDate,
    playersWithLeague: gameState.playersWithLeague,
    setGameStatus: gameState.setGameStatus,
    canSave: gameState.canSave,
    canStart: gameState.canStart,
    canDelete: gameState.canDelete,
    startError: gameState.startError,
    isLeagueComplete: gameState.isLeagueComplete,
    isOvertimeComplete: gameState.isOvertimeComplete,
  });

  // Current group students
  const currentGroupStudents = useMemo(() => {
    return MOCK_STUDENTS_BY_GROUP[gameSetup.currentGroupId] || [];
  }, [gameSetup.currentGroupId]);

  // Student average scores map
  const studentAverageScores = useMemo(() => {
    const map = new Map<string, number>();
    currentGroupStudents.forEach((s) => {
      map.set(s._id, s.averageScore);
    });
    return map;
  }, [currentGroupStudents]);

  // Available students (not yet added)
  const availableStudents = useMemo(() => {
    const addedIds = new Set(gameState.localPlayers.map((p) => p.id));
    const pendingIds = new Set(pendingSelections.map((p) => p.studentId));
    return currentGroupStudents
      .filter((s) => !addedIds.has(s._id) && !pendingIds.has(s._id))
      .map((s) => {
        const previousPlayer = gameSetup.previousGamePlayers.get(s._id);
        if (previousPlayer) {
          return {
            ...s,
            displayScore: previousPlayer.score,
            scoreSource: "지난 게임" as const,
          };
        }
        return {
          ...s,
          displayScore: s.averageScore,
          scoreSource: "평균" as const,
        };
      });
  }, [
    gameState.localPlayers,
    pendingSelections,
    gameSetup.previousGamePlayers,
    currentGroupStudents,
  ]);

  // Late join hook
  const lateJoin = useLateJoin({
    localPlayers: gameState.localPlayers,
    localLeagues: gameState.localLeagues,
    previousGamePlayers: gameSetup.previousGamePlayers,
    studentAverageScores,
    isIndependentMode: false, // demo 페이지에서는 항상 일반 모드
    onConfirmLateJoin: gameActions.handleConfirmLateJoinPlayer,
  });

  // Get pending student's predicted league
  const getPendingStudentLeague = useCallback(
    (studentId: string): string => {
      const previousPlayer = gameSetup.previousGamePlayers.get(studentId);
      const student = currentGroupStudents.find((s) => s._id === studentId);
      const score = previousPlayer?.score ?? student?.averageScore ?? 0;

      return calculateRecommendedLeague(
        score,
        gameState.localPlayers,
        gameState.localLeagues,
      );
    },
    [
      gameState.localPlayers,
      gameState.localLeagues,
      gameSetup.previousGamePlayers,
      currentGroupStudents,
    ],
  );

  // Get pending student's score (지난 게임 점수 > 평균 점수)
  const getPendingStudentScore = useCallback(
    (studentId: string): number => {
      const previousPlayer = gameSetup.previousGamePlayers.get(studentId);
      if (previousPlayer) {
        return previousPlayer.score;
      }
      return studentAverageScores.get(studentId) ?? 0;
    },
    [gameSetup.previousGamePlayers, studentAverageScores],
  );

  // Page unload warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (gameState.isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [gameState.isDirty]);

  // ============ Wrapped Handlers ============

  const handleAddPlayer = (studentId: string, studentName: string) => {
    const previousPlayer = gameSetup.previousGamePlayers.get(studentId);
    const score =
      previousPlayer?.score ?? studentAverageScores.get(studentId) ?? 0;
    const isFromPreviousGame = !!previousPlayer;
    gameActions.handleAddPlayer(
      studentId,
      studentName,
      score,
      isFromPreviousGame,
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    await gameActions.handleSave();
    setIsSaving(false);
  };

  const handleStartGame = async () => {
    setIsStarting(true);
    await gameActions.handleStartGame();
    setIsStarting(false);
  };

  // ============ Demo Reset ============

  const handleResetDemo = () => {
    gameState.setGameStatus("ready");
    gameSetup.resetToDefaults();
    gameState.setLocalPlayers([]);
    gameState.setSavedPromotionCount(1);
    gameState.setSavedLeagues([
      {
        name: "A",
        playerCount: 3,
        managedBy: null,
        matches: [],
        isFinished: false,
        isOvertime: false,
        overtimeMatches: [],
      },
      {
        name: "B",
        playerCount: 3,
        managedBy: null,
        matches: [],
        isFinished: false,
        isOvertime: false,
        overtimeMatches: [],
      },
    ]);
    gameState.setSavedPlayers([]);
    gameSetup.setPreviousGameDate("");
    gameSetup.setPreviousGamePlayers(new Map());
    gameState.setActiveLeagueTab("A");
    gameSetup.setCurrentGroupId("");
    gameSetup.setDialogGroupId("");
    gameSetup.setDialogLoadDate("");
    setPendingSelections([]);
    gameSetup.setIsDialogOpen(true);
    toast.success("데모가 초기화되었습니다.");
  };

  return (
    <PageContainer>
      {/* 게임 생성 다이얼로그 */}
      <CreateGameDialog
        open={gameSetup.isDialogOpen}
        onOpenChange={gameSetup.setIsDialogOpen}
        groups={MOCK_GROUPS}
        selectedGroupId={gameSetup.dialogGroupId}
        onGroupChange={gameSetup.setDialogGroupId}
        loadDate={gameSetup.dialogLoadDate}
        onLoadDateChange={gameSetup.setDialogLoadDate}
        onConfirm={gameSetup.handleDialogConfirm}
        hasTodayGame={gameSetup.hasTodayGame}
      />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Competition Demo</h1>
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">
            상태: <span className="font-medium">{gameState.gameStatus}</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleResetDemo}>
            데모 초기화
          </Button>
        </div>
      </div>

      {/* Validation warnings */}
      <ValidationWarnings
        playerCountMismatch={gameState.playerCountMismatch}
        hasUnassignedPlayers={gameState.hasUnassignedPlayers}
        totalCapacity={gameState.totalCapacity}
        playerCount={gameState.localPlayers.length}
        promotionCountError={gameState.promotionCountError}
      />

      {/* Header Row */}
      <GameHeader
        office={MOCK_OFFICE}
        currentGroupId={gameSetup.currentGroupId}
        groups={MOCK_GROUPS}
        previousGameDate={gameSetup.previousGameDate}
        isDirty={gameState.isDirty}
        canSave={gameState.canSave}
        canStart={gameState.canStart}
        canDelete={gameState.canDelete}
        isSaving={isSaving}
        isStarting={isStarting}
        onGroupChange={gameSetup.handleGroupChange}
        onPreviousGameDateChange={gameSetup.setPreviousGameDate}
        onLoadPreviousGame={gameSetup.handleLoadPreviousGame}
        onReset={gameActions.handleReset}
        onSave={handleSave}
        onStartGame={handleStartGame}
        onDeleteGame={gameActions.handleDeleteGame}
      />

      <div className="h-px bg-border mb-6" />

      {/* Playing State UI */}
      {gameState.gameStatus === "in_progress" && (
        <>
          <InProgressStateView
            leagues={gameState.localLeagues}
            activeLeagueTab={gameState.activeLeagueTab}
            availableStudents={availableStudents}
            pendingLateJoin={lateJoin.pendingLateJoin}
            onTabChange={gameState.setActiveLeagueTab}
            onSelectLateJoinPlayer={lateJoin.handleSelectLateJoinPlayer}
            onChangeLateJoinLeague={lateJoin.handleChangeLateJoinLeague}
            onConfirmLateJoin={lateJoin.handleConfirmLateJoin}
            onCancelLateJoin={lateJoin.handleCancelLateJoin}
            getLeaguePlayers={gameState.getLeaguePlayers}
            getTiedPlayers={gameState.getTiedPlayers}
            isLeagueComplete={gameState.isLeagueComplete}
            needsOvertime={gameState.needsOvertime}
            isOvertimeComplete={gameState.isOvertimeComplete}
            onRecordMatch={gameActions.handleRecordMatch}
            onDeleteMatch={gameActions.handleDeleteMatch}
            onRecordOvertimeMatch={gameActions.handleRecordOvertimeMatch}
            onDeleteOvertimeMatch={gameActions.handleDeleteOvertimeMatch}
            onStartOvertime={gameActions.handleStartOvertime}
            onFinishLeague={gameActions.handleFinishLeague}
          />

          {/* 종료된 리그 결과 (부분 결과) */}
          {gameState.hasFinishedLeagues && (
            <ResultsTable
              results={gameState.finalResults}
              promotionCount={parseInt(gameState.localPromotionCount) || 0}
              isPartial={true}
            />
          )}
        </>
      )}

      {/* Finished State UI */}
      {gameState.gameStatus === "done" && (
        <ResultsTable
          results={gameState.finalResults}
          promotionCount={parseInt(gameState.localPromotionCount) || 0}
        />
      )}

      {/* Ready State UI */}
      {gameState.gameStatus === "ready" && (
        <ReadyStateView
          currentUser={MOCK_CURRENT_USER}
          localPromotionCount={gameState.localPromotionCount}
          localLeagues={gameState.localLeagues}
          playersWithLeague={gameState.playersWithLeague}
          pendingSelections={pendingSelections}
          availableStudents={availableStudents}
          currentGroupStudents={currentGroupStudents}
          onPromotionCountChange={gameState.setLocalPromotionCount}
          onLeagueCountChange={gameActions.handleLeagueCountChange}
          onToggleManage={gameActions.handleToggleManage}
          onAddLeague={gameActions.handleAddLeague}
          onRemoveLeague={gameActions.handleRemoveLeague}
          onAddPlayer={handleAddPlayer}
          onRemovePlayer={gameActions.handleRemovePlayer}
          onPendingSelectionsChange={setPendingSelections}
          getPendingStudentLeague={getPendingStudentLeague}
          getPendingStudentScore={getPendingStudentScore}
        />
      )}

      {/* Debug Info */}
      <DebugInfo
        gameStatus={gameState.gameStatus}
        isDirty={gameState.isDirty}
        canSave={gameState.canSave}
        canStart={gameState.canStart}
        canDelete={gameState.canDelete}
        playerCountMismatch={gameState.playerCountMismatch}
        allLeaguesHaveManager={gameState.allLeaguesHaveManager}
        startError={gameState.startError}
        promotionCountError={gameState.promotionCountError}
        allLeaguesFinished={gameState.allLeaguesFinished}
        activeLeagueTab={gameState.activeLeagueTab}
        totalPlayers={gameState.localPlayers.length}
        leagues={gameState.localLeagues}
      />
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/competition/demo")({
  component: CompetitionDemoPage,
});
